/* =========================================================================
   NHASS — global.js
   - Reveal animations, mobile menu, header scroll state
   - AJAX cart drawer: ATC intercept, qty change, remove, Section Rendering API
   ========================================================================= */

(function () {
  'use strict';

  /* --- IntersectionObserver reveal --- */
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!prefersReduced && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
  } else {
    document.querySelectorAll('.reveal').forEach(el => el.classList.add('is-in'));
  }

  /* --- Header scroll state --- */
  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* --- Mobile menu --- */
  const menuBtn   = document.querySelector('[data-menu-toggle]');
  const mobileNav = document.querySelector('[data-mobile-nav]');
  if (menuBtn && mobileNav) {
    const close = () => {
      mobileNav.classList.remove('is-open');
      menuBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    };
    menuBtn.addEventListener('click', () => {
      const isOpen = mobileNav.classList.toggle('is-open');
      menuBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      document.body.style.overflow = isOpen ? 'hidden' : '';
    });
    mobileNav.addEventListener('click', (e) => {
      if (e.target.matches('[data-mobile-close]') || e.target.closest('a')) close();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }

  /* =========================================================================
     CART DRAWER
     ========================================================================= */
  const NHASS = window.NHASS = window.NHASS || {};

  const Cart = {
    sectionId: 'cart-drawer',

    drawer() { return document.querySelector('[data-cart-drawer]'); },

    open() {
      const d = this.drawer();
      if (!d) return;
      d.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },

    close() {
      const d = this.drawer();
      if (!d) return;
      d.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    setLoading(on) {
      const d = this.drawer();
      if (d) d.classList.toggle('is-loading', !!on);
    },

    refresh(sectionHtml) {
      if (!sectionHtml) return;
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, 'text/html');
      const newSection = doc.querySelector('#shopify-section-' + this.sectionId);
      const oldSection = document.querySelector('#shopify-section-' + this.sectionId);
      if (newSection && oldSection) {
        const oldDrawer = oldSection.querySelector('[data-cart-drawer]');
        const wasOpen = oldDrawer && oldDrawer.getAttribute('aria-hidden') === 'false';
        oldSection.replaceWith(newSection);
        if (wasOpen) {
          const newDrawer = newSection.querySelector('[data-cart-drawer]');
          if (newDrawer) {
            newDrawer.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
          }
        }
      }
    },

    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = count; });
    },

    async add(formData) {
      this.setLoading(true);
      formData.append('sections', this.sectionId);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/javascript' },
          body: formData
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart.');
        await this.fetchAndRefresh();
        this.open();
        return data;
      } catch (err) {
        console.error('[NHASS cart] add failed:', err);
        alert(err.message || 'Could not add to cart.');
        throw err;
      } finally {
        this.setLoading(false);
      }
    },

    async change(lineKey, quantity) {
      this.setLoading(true);
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
          body: JSON.stringify({ id: lineKey, quantity: quantity, sections: this.sectionId })
        });
        const data = await res.json();
        this.updateCount(data.item_count);
        if (data.sections && data.sections[this.sectionId]) {
          this.refresh(data.sections[this.sectionId]);
        } else {
          await this.fetchAndRefresh();
        }
      } catch (err) {
        console.error('[NHASS cart] change failed:', err);
      } finally {
        this.setLoading(false);
      }
    },

    async fetchAndRefresh() {
      const [cartRes, sectionRes] = await Promise.all([
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } }),
        fetch('/?sections=' + this.sectionId, { headers: { 'Accept': 'text/html' } })
      ]);
      const cart = await cartRes.json();
      const sections = await sectionRes.json();
      this.updateCount(cart.item_count);
      if (sections[this.sectionId]) this.refresh(sections[this.sectionId]);
    }
  };

  NHASS.cart = Cart;

  document.addEventListener('click', (e) => {
    const cartLink = e.target.closest('[data-cart-link]');
    if (cartLink) {
      e.preventDefault();
      Cart.open();
      return;
    }

    if (e.target.closest('[data-cart-close]')) {
      const link = e.target.closest('a[data-cart-close]');
      if (!link) e.preventDefault();
      Cart.close();
      return;
    }

    if (e.target.closest('[data-cart-checkout]')) {
      e.preventDefault();
      window.location.href = '/checkout';
      return;
    }

    const decBtn = e.target.closest('[data-line-decrease]');
    const incBtn = e.target.closest('[data-line-increase]');
    if (decBtn || incBtn) {
      e.preventDefault();
      const item = (decBtn || incBtn).closest('[data-line-key]');
      if (!item) return;
      const key = item.dataset.lineKey;
      const qtyEl = item.querySelector('[data-line-qty]');
      const cur = parseInt((qtyEl && (qtyEl.textContent || qtyEl.value)) || '1', 10);
      const next = decBtn ? Math.max(0, cur - 1) : cur + 1;
      Cart.change(key, next);
      return;
    }

    const remBtn = e.target.closest('[data-line-remove]');
    if (remBtn) {
      e.preventDefault();
      const item = remBtn.closest('[data-line-key]');
      if (!item) return;
      Cart.change(item.dataset.lineKey, 0);
      return;
    }
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const fd = new FormData(form);
    Cart.add(fd).catch(() => {});
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const d = Cart.drawer();
      if (d && d.getAttribute('aria-hidden') === 'false') Cart.close();
    }
  });
})();
